/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:39:04AM]
Permissions to update outside the main branch: [true]
*/

import { Button, DesignType, Input, ModalConfirm, useCondition } from '@finbeat/react-toolkit';
import classNames from 'classnames';
import LanguageSelector from 'components/LanguageSelector/LanguageSelector';
import { dateFormat } from 'constans/dateFormat';
import { addDays, format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { MenuItemType, useSidebarContext } from 'providers/SidebarProvider';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineFolderOpen, AiOutlineStar } from 'react-icons/ai';
import { FaBars, FaPaintRoller } from 'react-icons/fa';
import { FiAlertTriangle, FiSettings } from 'react-icons/fi';
import { MdBorderColor, MdOutlineLogin, MdOutlineLogout, MdOutlinePerson } from 'react-icons/md';
import {
    Menu,
    MenuItem,
    ProSidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SubMenu
} from 'react-pro-sidebar';
import 'react-pro-sidebar/dist/css/styles.css';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';
import 'scss/Main.scss';
import i18n from 'utils/i18n';
import { OrderStatusesMenu } from '../OrderStatusesSidebar/OrderStatusesSidebar';
import './Sidebar.scss';
import SidebarHeaderNova from './SidebarHeader.nova';
import SidebarMenuItem from './SidebarMenuItem/SidebarMenuItem';
import SidebarSelector from './SidebarSelector/SidebarSelector';

export default function Sidebar() {
    const { t } = useTranslation();
    const { auth, hasRole } = useAuth();
    const {
        currentTab,
        menu,
        favoritesMenu,
        state,
        filter,
        setFilter,
        toggleState,
        updateFavorites,
        itemIsFavorites,
        setCurrentTab,
        accordionOpened,
        setAccordionOpened,
    } = useSidebarContext();

    const sidebarRef = useRef<HTMLDivElement>(null);

    const {
        instanceName,
        generalTechnicalWork,
        designType,
        setDesignType,
        designTypes,
        settings,
        operationDay,
        operationalDate,
        operationalNextDate,
        updateConfigMutation,
        dateFormat: localDateFormat
    } = useGeneralSettingsContext();

    const [operationDayModal, setOperationDayModal] = useState(false);

    const getMenuItem = (item: MenuItemType, isVisiblePin = true, favoritMenu = false) => (
        <SidebarMenuItem
            key={item.value}
            {...item}
            id={item.id ?? ''}
            isActive={item.id === currentTab}
            onSetFavorites={(id: string) => updateFavorites(id)}
            onClick={() => {
                setCurrentTab(item.id ?? '');
            }}
            isSidebarCollapsed={state}
            isFavorites={itemIsFavorites(item.id)}
            isVisiblePin={isVisiblePin}
            favoritMenu={favoritMenu}
        />
    );

    const getMenuItemWithChilds = (item: MenuItemType) => {
        if (!item.childs) {
            return getMenuItem(item);
        } else {
            return (
                <SubMenu
                    placeholder={undefined}
                    onPointerEnterCapture={() => undefined}
                    onPointerLeaveCapture={() => undefined}
                    style={{
                        direction: 'initial',
                        marginBottom: '2px',
                    }}
                    key={item.value}
                    title={t(item.value)}
                    icon={item.icon}
                    defaultOpen={item.open}
                >
                    {item.childs.map((x) => getMenuItemWithChilds(x))}
                </SubMenu>
            );
        }
    };

    const merchantInstructionLink = settings
        .find((x) => x.code === 'MerhcantInstructionLink')
        ?.translations.find((x) => x.cultureCode === i18n.language)?.value;

    const accordionBodyMenuClass = useMemo(
        () => `pro-menu_${state ? 'closed' : 'opened'}`,
        [state]
    );

    const toggle = (id: string) => {
        !filter && setAccordionOpened((prev) => (prev === id ? '' : id));
    };

    const accordionProps = { toggle: toggle, className: 'sidebar__accordion', flush: true };

    const header = useCondition(
        designType,
        {
            nova: <SidebarHeaderNova state={state} toggleState={toggleState} />,
        },
        <SidebarHeader>
            <Menu iconShape="square" onClick={toggleState}>
                <MenuItem icon={<FaBars />}>{instanceName}</MenuItem>
            </Menu>
        </SidebarHeader>
    );

    const formatedDate = format(operationalDate, localDateFormat);

    const operationText = useMemo(
        () => (
            <>
                <p>{t('BackOfficeOperationDate', { currentDay: formatedDate })}</p>
                <p>{t('BackOfficeOperationDayStatus')}: {operationDay ? t('BackOfficeOperationDayOpenStatus') : t('BackOfficeOperationDayClosedStatus')}</p>
                <p>{!operationDay ? t('BackOfficeOperationDayOpenMessage') : t('BackOfficeOperationDayCloseMessage')}</p>
            </>
        ),
        [t, operationDay, operationalDate, operationalNextDate]
    );

    return (
        <ProSidebar
            collapsed={state}
            className={classNames('sidebar', { 'sidebar-portal-user': auth?.portalUserEmployerId })}
            data-testid="sidebar"
        >
            {header}
            {auth?.user != null && !generalTechnicalWork && (
                <Input
                    value={filter}
                    name="filter"
                    placeholder={`${t('BackOfficeSearch')}...`}
                    onChange={(e) => setFilter(e.target.value)}
                    className={classNames('sidebar__filter-container', state && 'collapsed')}
                    inputClassName="sidebar__filter"
                    autoComplete="off"
                    isFloating={false}
                />
            )}
            <SidebarContent className="custom-scrollbar" ref={sidebarRef}>
                {auth?.user != null && (
                    <>
                        {(auth.roles == null || auth.roles?.length === 0) && (
                            <Menu iconShape="square" className="sidebar__workplace-settings-menu">
                                <SidebarMenuItem
                                    id="workplacesettings"
                                    icon={<FiAlertTriangle />}
                                    value="BackOfficeUsersRolesNotFound"
                                    isVisiblePin={false}
                                />
                            </Menu>
                        )}
                        {!generalTechnicalWork && !auth.portalUserEmployerId && (
                            <>
                                <Accordion open={accordionOpened} {...accordionProps}>
                                    {favoritesMenu.length > 0 && (
                                        <AccordionItem>
                                            <AccordionHeader tag="div" targetId="favorites">
                                                <Menu iconShape="square">
                                                    <SidebarMenuItem
                                                        icon={<AiOutlineStar />}
                                                        id={'favorites-menu'}
                                                        value="BackOfficeFavorites"
                                                        isVisiblePin={false}
                                                    />
                                                </Menu>
                                            </AccordionHeader>
                                            <AccordionBody accordionId="favorites">
                                                {favoritesMenu.length > 0 ? (
                                                    <Menu
                                                        iconShape="square"
                                                        className={accordionBodyMenuClass}
                                                    >
                                                        {favoritesMenu.map((item) =>
                                                            getMenuItem(item, false, true)
                                                        )}
                                                    </Menu>
                                                ) : (
                                                    t('BackOfficeNotFound')
                                                )}
                                            </AccordionBody>
                                        </AccordionItem>
                                    )}
                                    {hasRole('orderGet') && (
                                        <OrderStatusesMenu menuClass={accordionBodyMenuClass} />
                                    )}{' '}
                                </Accordion>
                                {hasRole('merchantGet') && (
                                    <Menu iconShape="square">
                                        <SidebarMenuItem
                                            id="merchant"
                                            icon={<MdBorderColor />}
                                            value="BackOfficeMerchant"
                                            link="/merchant"
                                            isVisiblePin={false}
                                            isActive={currentTab === 'merchant'}
                                            onClick={() => {
                                                setCurrentTab('merchant');
                                            }}
                                        />
                                    </Menu>
                                )}
                                {hasRole('preordersGet') && (
                                    <Menu iconShape="square">
                                        <SidebarMenuItem
                                            id="preorder"
                                            icon={<MdBorderColor />}
                                            value="BackOfficePreorders"
                                            link="/preorder"
                                            isVisiblePin={false}
                                            isActive={currentTab === 'preorder'}
                                            onClick={() => {
                                                setCurrentTab('preorder');
                                            }}
                                        />
                                    </Menu>
                                )}
                                {hasRole('clientsGet') && (
                                    <Menu iconShape="square">
                                        <SidebarMenuItem
                                            id="clients"
                                            icon={<MdOutlinePerson />}
                                            value="BackOfficeClients"
                                            link="/clients"
                                            isVisiblePin={false}
                                            isActive={currentTab === 'clients'}
                                            onClick={() => {
                                                setCurrentTab('clients');
                                            }}
                                        />
                                    </Menu>
                                )}

                                {menu.length > 0 && (
                                    <Accordion open={accordionOpened} {...accordionProps}>
                                        <AccordionItem>
                                            <AccordionHeader tag="div" targetId="menu">
                                                <Menu iconShape="square">
                                                    <SidebarMenuItem
                                                        icon={<AiOutlineFolderOpen />}
                                                        id="general-menu"
                                                        value="BackOfficeMenu"
                                                        isVisiblePin={false}
                                                    />
                                                </Menu>
                                            </AccordionHeader>
                                            <AccordionBody accordionId="menu">
                                                <Menu
                                                    iconShape="square"
                                                    className={accordionBodyMenuClass}
                                                >
                                                    {menu.map((item) =>
                                                        getMenuItemWithChilds(item)
                                                    )}
                                                </Menu>
                                            </AccordionBody>
                                        </AccordionItem>
                                    </Accordion>
                                )}
                            </>
                        )}
                        {auth.portalUserEmployerId && (
                            <Menu iconShape="square" className={accordionBodyMenuClass}>
                                {menu.map((item) => getMenuItemWithChilds(item))}
                            </Menu>
                        )}
                        {hasRole('workplaceSettingsGetAll') && !auth.portalUserEmployerId && (
                            <Menu iconShape="square" className="sidebar__workplace-settings-menu">
                                <SidebarMenuItem
                                    id="workplacesettings"
                                    icon={<FiSettings />}
                                    value="BackOfficeWorkplaceSettings"
                                    link="/workplacesettings"
                                    isVisiblePin={false}
                                    isActive={currentTab === 'workplacesettings'}
                                    onClick={() => {
                                        setCurrentTab('workplacesettings');
                                    }}
                                />
                            </Menu>
                        )}
                    </>
                )}
            </SidebarContent>
            {auth?.user != null && (
                <Menu>
                    {hasRole(['configUpdate', 'configGetById']) && (
                        <>
                            <ModalConfirm
                                content={operationText}
                                confirmButtonContent={t('BackOfficeYes')}
                                cancelButtonContent={t('BackOfficeNo')}
                                isOpen={operationDayModal}
                                toggle={(v) => {
                                    setOperationDayModal(!operationDayModal);
                                    if (v) {
                                        updateConfigMutation.mutate({
                                            name: 'OperationalDayOpen',
                                            value: operationDay ? '0' : '1',
                                        });
                                        if (operationDay) {
                                            updateConfigMutation.mutate({
                                                name: 'OperationalDate',
                                                value: format(addDays(operationalDate, 1), dateFormat),
                                            });
                                            updateConfigMutation.mutate({
                                                name: 'OperationalNextDate',
                                                value: format(addDays(operationalNextDate, 1), dateFormat),
                                            });

                                        }
                                    }
                                }}
                            />
                            <MenuItem>
                                <Button
                                    color={operationDay ? 'danger' : 'success'}
                                    disabled={updateConfigMutation.isLoading}
                                    onClick={() => setOperationDayModal(true)}
                                    className="w-100 open-day-button"
                                >
                                    {!operationDay
                                        ? t('BackOfficeOperationDayOpen')
                                        : t('BackOfficeOperationDate', {
                                            currentDay: formatedDate,
                                        })}
                                </Button>
                            </MenuItem>
                        </>
                    )}
                    {auth.showMerchantInstruction && merchantInstructionLink && (
                        <MenuItem>
                            <a href={merchantInstructionLink} rel="noreferrer" target="_blank">
                                {t('BackOfficeMerchantInstructionLink')}
                            </a>
                        </MenuItem>
                    )}
                </Menu>
            )}{' '}
            <SidebarFooter className="sidebar__footer">
                <Menu iconShape="square">
                    {auth?.user != null && designTypes?.length && (
                        <SidebarSelector<DesignType>
                            icon={<FaPaintRoller />}
                            value={designType}
                            items={designTypes}
                            onSelect={(e) => e.id && setDesignType(e.id)}
                            isSidebarCollapsed={state}
                        />
                    )}
                    <LanguageSelector isSidebarCollapsed={state} />
                    {auth?.user == null ? (
                        <SidebarMenuItem
                            icon={<MdOutlineLogin />}
                            value="BackOfficeEnter"
                            id="login"
                            link={'/login'}
                        />
                    ) : (
                        <SidebarMenuItem
                            icon={<MdOutlineLogout />}
                            id="logout"
                            link={'/logout'}
                            value={auth?.userFullName ?? ''}
                        />
                    )}
                </Menu>
            </SidebarFooter>
        </ProSidebar>
    );
}
